import { type EntityType, WorkflowType } from '@communities/content-management/domain';
import { isCommunity } from '@communities/content-management/utils';
import { TabNavigation as CoreTabNavigation } from '@core/components/TabNavigation';
import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';

import { CaseSearchTab } from './CaseSearchTab';
import { ClassificationTab } from './ClassificationTab';
import { CommunityContextTab } from './CommunityContextTab';
import { IdentityAccessTab } from './IdentityAccessTab';
import { LandingPageTab } from './LandingPageTab';
import { ModerationTab } from './ModerationTab';
import { StrikeTab } from './StrikeTab';

interface TabNavigationProps {
  entityId: string;
  entityType: EntityType;
  isEntityAdmin: boolean;
  communityGroupId?: string;
  onEditCommunity?: () => void;
}

interface BuildTabsOptions {
  entityId: string;
  entityType: EntityType;
  isEntityAdmin: boolean;
  communityGroupId?: string;
  isModerationEnabled: boolean;
  isStrikeEnabled: boolean;
  isOverviewEnabled: boolean;
  isCaseSearchEnabled: boolean;
  onEditCommunity?: () => void;
}

function buildCommunityTabs({
  entityId,
  communityGroupId,
  isEntityAdmin,
  isModerationEnabled,
  isStrikeEnabled,
  isOverviewEnabled,
  isCaseSearchEnabled,
  onEditCommunity,
}: BuildTabsOptions) {
  const tabs = [];

  // Overview tab - visible to ALL users for communities when feature flag is enabled
  if (isOverviewEnabled) {
    tabs.push({
      key: 'overview',
      label: 'Overview',
      content: <LandingPageTab communityGroupId={communityGroupId} communityId={entityId} />,
    });
  }

  // Community Context tab - admin only
  if (isEntityAdmin) {
    tabs.push({
      key: 'community-context',
      label: 'Community Context',
      content: <CommunityContextTab entityId={entityId} isEntityAdmin={isEntityAdmin} onEditCommunity={onEditCommunity} />,
    });
  }

  // Moderation tab - visible to admins only when feature flag is enabled
  if (isModerationEnabled && isEntityAdmin) {
    tabs.push({
      key: 'moderation',
      label: 'Moderation',
      content: <ModerationTab communityId={entityId} isEntityAdmin={isEntityAdmin} />,
    });
  }

  // Strike tab - visible when feature flag is enabled
  if (isStrikeEnabled) {
    tabs.push({
      key: 'strike',
      label: 'Strike',
      content: <StrikeTab communityId={entityId} isEntityAdmin={isEntityAdmin} />,
    });
  }

  // Case Search tab - visible when feature flag is enabled
  if (isCaseSearchEnabled) {
    tabs.push({
      key: 'case-search',
      label: 'Case Search',
      content: <CaseSearchTab communityId={entityId} />,
    });
  }

  return tabs;
}

function buildTabs({
  entityId,
  entityType,
  isEntityAdmin,
  communityGroupId,
  isModerationEnabled,
  isStrikeEnabled,
  isOverviewEnabled,
  isCaseSearchEnabled,
  onEditCommunity,
}: BuildTabsOptions) {
  const isCommunityEntity = isCommunity(entityType);

  const communityTabs = isCommunityEntity
    ? buildCommunityTabs({
        entityId,
        entityType,
        isEntityAdmin,
        communityGroupId,
        isModerationEnabled,
        isStrikeEnabled,
        isOverviewEnabled,
        isCaseSearchEnabled,
        onEditCommunity,
      })
    : [];

  const overview = communityTabs.filter(t => t.key === 'overview');
  const communityContext = communityTabs.filter(t => t.key === 'community-context');
  const moderationAndStrike = communityTabs.filter(t => t.key === 'moderation' || t.key === 'strike');
  const caseSearch = communityTabs.filter(t => t.key === 'case-search');

  const adminTabs = isEntityAdmin
    ? [
        {
          key: 'identity-access',
          label: 'Identity & Access',
          content: <IdentityAccessTab entityId={entityId} entityType={entityType} isEntityAdmin={isEntityAdmin} workflowType={WorkflowType.CLASSIFICATION} />,
        },
      ]
    : [];

  const classificationTab = isEntityAdmin
    ? [
        {
          key: 'classification',
          label: 'Classification',
          content: <ClassificationTab entityId={entityId} entityType={entityType} isEntityAdmin={isEntityAdmin} workflowType={WorkflowType.CLASSIFICATION} />,
        },
      ]
    : [];

  // Order: Overview, Identity & Access, Community Context, Classification, Moderation, Strike, Case Search
  return [...overview, ...adminTabs, ...communityContext, ...classificationTab, ...moderationAndStrike, ...caseSearch];
}

export function TabNavigation({ entityId, entityType, isEntityAdmin, communityGroupId, onEditCommunity }: TabNavigationProps) {
  const isModerationEnabled = useIsFeatureEnabled(FEATURE_FLAGS.MODERATION);
  const isStrikeEnabled = useIsFeatureEnabled(FEATURE_FLAGS.STRIKE);
  const isOverviewEnabled = useIsFeatureEnabled(FEATURE_FLAGS.COMMUNITY_OVERVIEW);
  const isCaseSearchEnabled = useIsFeatureEnabled(FEATURE_FLAGS.CASE_SEARCH);

  const isCommunityEntity = isCommunity(entityType);
  const tabs = buildTabs({
    entityId,
    entityType,
    isEntityAdmin,
    communityGroupId,
    isModerationEnabled,
    isStrikeEnabled,
    isOverviewEnabled,
    isCaseSearchEnabled,
    onEditCommunity,
  });
  const defaultTab = isCommunityEntity && isOverviewEnabled ? 'overview' : 'identity-access';

  return (
    <CoreTabNavigation ariaLabel={`${isCommunityEntity ? 'Community' : 'Community group'} management sections`} defaultActiveTab={defaultTab} tabs={tabs} />
  );
}
